import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom'
import {
  HomeIcon,
  UserIcon,
  ArrowRightOnRectangleIcon,
  SparklesIcon,
  XMarkIcon,
  Bars3Icon
} from '@heroicons/react/24/outline'
import { useState, useEffect, useRef, useCallback, ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useAuth } from '../contexts/AuthContext'
import { useSageMode } from '../contexts/SageModeContext'
import { isAdminUser } from '../utils/admin'
import { initAffiliateTracking } from '../utils/affiliate-tracking'
import AuthModal from './auth/AuthModal'
import SageModeOverlay from './SageModeOverlay'
import { supabase } from '../lib/supabase'
import Footer from './Footer'
import { ClockWidget } from './ui/clock-widget'
import { VerseTicker } from './ui/verse-ticker'
import NavLinks from './NavLinks'
import { LoadingOverlay } from './Loading'
import AffiliateBanner from './affiliate/AffiliateBanner'
import { useGallery } from '../contexts/GalleryContext'

export default function Layout({ children }: { children?: ReactNode }) {
  const location = useLocation()
  const { user, tier, signOut, loading, clearAuthStorage } = useAuth()
  // Derived synchronously from auth state — no extra DB round-trip needed for UI gating
  const userIsAdmin = !loading && !!user && isAdminUser(user)
  // Header widgets (clock + date) stand in for the dashboard's full-size ones,
  // so they belong to the signed-in admin dashboard only. Being on an /admin
  // URL isn't enough: the sign-in gate lives there too, and a locked-out
  // visitor shouldn't see dashboard chrome floating over the logo.
  //
  // The member dashboard (/dashboard) gets the same header clock + verse: it
  // has its own full-size clock and calendar to hand over to, and every
  // signed-in person who reaches it is looking at their own dashboard.
  const isMemberDashboard = location.pathname === '/dashboard' && !loading && !!user
  const isAdminRoute = (location.pathname.startsWith('/admin') && userIsAdmin) || isMemberDashboard
  const [headerNow, setHeaderNow] = useState(new Date())
  useEffect(() => {
    if (!isAdminRoute) return
    const t = setInterval(() => setHeaderNow(new Date()), 60_000)
    return () => clearInterval(t)
  }, [isAdminRoute])

  // The header clock is a stand-in for the full-size one in the dashboard's
  // widget row, so it fades out once that row scrolls into view. Measured on
  // scroll rather than with IntersectionObserver, which never fires in the
  // embedded preview browser.
  const [bigWidgetsVisible, setBigWidgetsVisible] = useState(false)
  useEffect(() => {
    if (!isAdminRoute) return
    let frame = 0
    const measure = () => {
      frame = 0
      // Track only the first row (clock / calendar / calculator), not the whole
      // grid — anchoring to the full grid kept the ticker up until every one of
      // the twelve cells had scrolled past.
      // The member dashboard marks its clock/calendar block directly.
      const el = document.querySelector('[data-header-widgets-anchor]')
        ?? document.getElementById('dashboard-widgets')?.firstElementChild
      if (!el) return setBigWidgetsVisible(false)
      const r = el.getBoundingClientRect()
      // Track the row's centre rather than its edges, so entering and leaving
      // are symmetric. Edge tests misbehave at both ends: the row peeks in at
      // the top of the page (handing over before you've scrolled), and its
      // bottom edge lingers on screen while you're already reading the second
      // row (holding the verse too long).
      const centre = r.top + r.height / 2
      setBigWidgetsVisible(centre > 0 && centre < window.innerHeight)
    }
    const onScroll = () => { if (!frame) frame = requestAnimationFrame(measure) }
    measure()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)
    // Interval fallback: covers both the dashboard mounting after Layout and
    // environments that don't deliver scroll events (the embedded preview
    // browser is one). A rect read every 250ms is negligible.
    const poll = setInterval(measure, 250)
    return () => {
      if (frame) cancelAnimationFrame(frame)
      clearInterval(poll)
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
    }
  }, [isAdminRoute])
  const [menuOpen, setMenuOpen] = useState(false)
  const [authModalOpen, setAuthModalOpen] = useState(false)
  const [userSubdomain, setUserSubdomain] = useState<string | null>(null)
  const [headerHeight, setHeaderHeight] = useState(80)
  const fixedHeaderRef = useRef<HTMLDivElement>(null)

  // State from NavLinks component needs to be lifted if we want to sync between desktop/mobile,
  // but for simple navigation, independent state in NavLinks is fine (submenus reset on close).

  const [homeHeaderReady, setHomeHeaderReady] = useState(location.pathname !== '/')
  const [isRouteLoading, setIsRouteLoading] = useState(false)
  const previousPathRef = useRef(location.pathname)
  const menuRef = useRef<HTMLDivElement>(null)
  // Any signed-in user gets the nav menu — it's the entry point for the role
  // switcher, profile, and sign-out. Previously gated to admins only.
  const showHeaderMenu = !loading && !!user
  // Show advertising banner above nav for visitors on the homepage
  const showAdBanner = location.pathname === '/' && !userIsAdmin && !loading
  const { activeGallery, closeGallery } = useGallery()
  const { state: sageModeState, toggleSageMode } = useSageMode()
  const navigate = useNavigate()

  // Load user subdomain for profile link
  useEffect(() => {
    // Initialize affiliate tracking from URL - captures ?ref= everywhere
    initAffiliateTracking();

    if (user) {
      const loadSubdomain = async () => {
        try {
          const { data } = await supabase
            .from('user_subdomains')
            .select('subdomain')
            .eq('user_id', user.id)
            .maybeSingle()
          if (data?.subdomain) {
            setUserSubdomain(data.subdomain)
          }
        } catch {
          // Ignore errors
        }
      }
      loadSubdomain()
    }
  }, [user]);

  // Cleanup ref is removed as it's no longer used

  useEffect(() => {
    // Disable body scroll when mobile menu is open
    if (menuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [menuOpen])

  // Scroll to top whenever the route changes
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
  }, [location.pathname])

  // Site-wide page view tracking for the admin Site Analytics dashboard.
  // Fires once per route change; visitor_id matches the pattern used by BlogPost.tsx
  // so unique-visitor counting is consistent across the whole site.
  useEffect(() => {
    let visitorId = localStorage.getItem('tlau_visitor_id')
    if (!visitorId) {
      visitorId = crypto.randomUUID()
      localStorage.setItem('tlau_visitor_id', visitorId)
    }

    fetch('/api/admin/analytics/record', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event_type: 'page_view',
        resource_id: location.pathname,
        metadata: { title: document.title, visitor_id: visitorId, referrer: document.referrer },
      }),
    }).catch(() => {})
  }, [location.pathname])

  // Route change loading animation (skip for homepage)
  useEffect(() => {
    const isNavigatingFromHome = previousPathRef.current === '/' && location.pathname !== '/'
    const isNavigatingToNonHome = previousPathRef.current !== location.pathname && location.pathname !== '/'
    // A walkthrough moving between pages asks to skip the animation: it is
    // already drawing over the page, and the cat would cover the target.
    const skipAnimation = (location.state as { skipRouteLoading?: boolean } | null)?.skipRouteLoading === true

    if (!skipAnimation && (isNavigatingFromHome || isNavigatingToNonHome)) {
      setIsRouteLoading(true)
      const timer = setTimeout(() => {
        setIsRouteLoading(false)
      }, 1200) // Show loading for 1.2 seconds

      previousPathRef.current = location.pathname
      return () => clearTimeout(timer)
    }

    previousPathRef.current = location.pathname
  }, [location.pathname])


  useEffect(() => {
    // Non-root pages: always show header immediately
    if (location.pathname !== '/') {
      setHomeHeaderReady(true)
      return
    }

    // Root page, still loading auth: hide header until we know who the visitor is
    if (loading) {
      setHomeHeaderReady(false)
      return
    }

    // Root page, visitor (non-admin): show header immediately — no animation to wait for
    if (!userIsAdmin) {
      setHomeHeaderReady(true)
      return
    }

    // Root page, admin: wait for the brand intro animation to signal completion
    setHomeHeaderReady(false)
    const handleAnimationComplete = () => setHomeHeaderReady(true)
    window.addEventListener('home-animation-complete', handleAnimationComplete)
    return () => {
      window.removeEventListener('home-animation-complete', handleAnimationComplete)
    }
  }, [location.pathname, loading, userIsAdmin])

  // Track fixed header height so main content padding and sticky elements stay correct
  useEffect(() => {
    const el = fixedHeaderRef.current
    if (!el) return
    const update = () => {
      const h = el.offsetHeight
      setHeaderHeight(h)
      // CSS custom property so any child (e.g. PhotoGallery sticky toolbar)
      // can stick just below the fixed header without hardcoding a pixel value.
      document.documentElement.style.setProperty('--nav-height', `${h}px`)
    }
    const ro = new ResizeObserver(update)
    ro.observe(el)
    update()
    return () => ro.disconnect()
  }, [showAdBanner])

  // Click-outside handler is removed as the menu is now a full-screen portal

  const handleLoginClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    // Close menu and open modal
    setMenuOpen(false)
    setAuthModalOpen(true)
  }

  const handleSignOut = async () => {
    await signOut()
    setMenuOpen(false)
    // Force a hard reload to ensure all auth state is cleared
    window.location.href = '/'
  }

  // Mouse position tracking is removed as it's no longer used

  // Hover handlers are removed as we're moving to a full-screen menu

  // Full-Screen Menu Portal Component (Mobile & Desktop)
  const MenuPortal = () => {
    if (!menuOpen) return null;

    return createPortal(
      <div
        data-sage-chrome
        className="fixed inset-0 z-[99999] bg-black flex flex-col"
        style={{ touchAction: 'none' }}
      >
        {/* Header Row (for close button) */}
        <div className="flex items-center justify-between h-16 w-full px-4 shrink-0">
          <div className="flex items-center h-12">
            <span className="text-white text-sm font-bold">MENU</span>
          </div>
          <button
            onClick={() => setMenuOpen(false)}
            className="flex items-center justify-center h-12 w-12 text-white"
            aria-label="Close menu"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="py-4">
            <NavLinks
              user={user}
              userIsAdmin={userIsAdmin}
              userSubdomain={userSubdomain}
              sageModeState={sageModeState}
              toggleSageMode={toggleSageMode}
              handleSignOut={handleSignOut}
              onLinkClick={() => setMenuOpen(false)}
              onLoginClick={handleLoginClick}
            />
          </div>
        </div>
      </div>,
      document.body
    );
  };

  return (
    <div className="min-h-screen bg-black flex flex-col">
      {/* Route transition loading overlay */}
      {isRouteLoading && <LoadingOverlay />}

      <SageModeOverlay />
      {/* THE AFFILIATE PROGRAM banner — sits above the nav for homepage visitors.
          It scrolls away with the page rather than riding in the fixed header:
          pinned, Instagram's in-app browser smeared copies of it down the screen. */}
      {showAdBanner && <AffiliateBanner noMargin />}
      {/* data-sage-chrome keeps the header clickable while SAGE MODE is armed,
          so you can always reach the menu and switch it back off.
          Sticky under the banner on the homepage, fixed everywhere else. */}
      <div
        ref={fixedHeaderRef}
        data-sage-chrome
        className={`${showAdBanner ? 'sticky' : 'fixed'} top-0 left-0 w-full bg-black z-[999]`}
      >

        <nav className="w-full backdrop-blur-md">
          <div className="w-full px-4 sm:px-6 lg:px-8">
            {/* Top row */}
            <div
              className="relative flex items-center pt-3 pb-7 gap-4 flex-nowrap"
              style={{
                opacity: homeHeaderReady ? 1 : 0,
                pointerEvents: homeHeaderReady ? 'auto' : 'none',
                transition: 'opacity 0.6s ease-in-out',
                minHeight: '84px',
              }}
            >
              {/* Persistent back button — shown in nav whenever a gallery is open */}
              {activeGallery && location.pathname === '/' && (
                <button
                  onClick={closeGallery}
                  className="absolute left-0 flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.3em] text-white/50 hover:text-white transition-colors"
                >
                  ← Gallery
                </button>
              )}

              {/* Admin header widgets — absolutely centered to the page, so they
                  sit mid-header regardless of the logo and menu on either side.
                  ClockWidget draws its face with `absolute inset-0`, so it needs
                  an explicit width or it collapses to nothing.

                  The slot always shows something: while the full-size clock and
                  calendar are on screen the mini versions are redundant, so it
                  crossfades to the daily verse. Both layers stay mounted and
                  stacked — mounting/unmounting can't crossfade. */}
              {isAdminRoute && (
                <div className="absolute left-1/2 -translate-x-1/2 top-3 z-10 h-[46px] w-[55vw] max-w-[520px] leading-none pointer-events-none">
                  <div
                    className={`absolute inset-0 flex items-center justify-center gap-2 transition-opacity duration-500 ${
                      bigWidgetsVisible ? 'opacity-0' : 'opacity-100 pointer-events-auto'
                    }`}
                  >
                    <ClockWidget size="lg" hideLabel lockMode className="w-[64px] !min-h-[46px]" />
                    <Link
                      to={userIsAdmin ? '/admin?panel=calendar' : '/dashboard'}
                      title={userIsAdmin ? 'Open master calendar' : 'My dashboard'}
                      // Matched to the mini clock's digits rather than styled
                      // on its own: the clock draws them at 15cqw of its 64px
                      // box — 9.6px, bold, mono, tabular — so the date is the
                      // same face at the same size instead of a larger,
                      // lighter, letter-spaced one beside it.
                      className="flex items-center h-[46px] font-mono font-bold tabular-nums text-white hover:text-white/70 transition-colors"
                      style={{ fontSize: '9.6px', letterSpacing: 0 }}
                    >
                      {`${headerNow.getMonth() + 1}.${headerNow.getDate()}.${headerNow.getFullYear()}`}
                    </Link>
                  </div>
                  <div
                    className={`absolute inset-0 flex items-center justify-center transition-opacity duration-500 ${
                      bigWidgetsVisible ? 'opacity-100 pointer-events-auto' : 'opacity-0'
                    }`}
                  >
                    <VerseTicker className="w-full" />
                  </div>
                </div>
              )}

              {/* Logo — centered for visitors, left-aligned when the menu shows */}
              <Link
                to="/"
                // `sage-brand` is the hook SAGE MODE tints gold — see index.css.
                className={`sage-brand flex items-center hover:opacity-70 transition-opacity ${
                  showHeaderMenu ? 'flex-shrink-0' : 'absolute left-1/2 -translate-x-1/2 top-0'
                }`}
                style={{ display: 'flex', alignItems: 'center' }}
              >
                <img
                  src="/logo.png"
                  alt="THE LOST+UNFOUNDS"
                  style={{ height: showHeaderMenu ? '63px' : '84px', width: 'auto', display: 'block' }}
                />
              </Link>
              {showHeaderMenu && (
                <div className="flex items-center space-x-4 ml-auto flex-shrink-0 leading-none h-12">
                  {/* Admin-only header widgets: clock at roughly logo height with
                      the date beneath it. ClockWidget draws its face with
                      `absolute inset-0`, so it needs an explicit width or it
                      collapses to nothing. Label hidden to keep the bar sleek —
                      tapping the face still cycles clock → stopwatch → timer. */}
                  <div className="header-nav" ref={menuRef}>
                    <button
                      type="button"
                      className="menu-toggle flex items-center justify-center h-12 w-12 leading-none"
                      onClick={(e) => {
                        e.stopPropagation();
                        setMenuOpen(!menuOpen);
                      }}
                      aria-label="Toggle menu"
                      aria-expanded={menuOpen}
                    >
                      <Bars3Icon className="w-6 h-6 text-white" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </nav>
      </div>

      {/* Render Menu Portal */}
      <MenuPortal />

      <main className="pb-6 flex-1" style={{ paddingTop: showAdBanner ? 0 : headerHeight }}>
        {children || <Outlet />}
      </main>
      <Footer />
      <AuthModal isOpen={authModalOpen} onClose={() => setAuthModalOpen(false)} />

    </div>
  );
}

